import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PublicSlimHeader from '../../Common/PublicSlimHeader';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { uploadFilePublic } from '../../../services/fileApi';
import {
  getPublicSponsorContractPayment,
  initiatePublicSponsorContractCardPayment,
  submitPublicSponsorContractOfflinePayment,
  downloadPublicSponsorContractTaxInvoicePdf,
  downloadPublicSponsorContractReceiptPdf,
  getPublicEventSettings,
} from '../../../services/eventApi';
import { triggerBlobDownload } from '../../../utils/eventProformaStaffDownloads';
import { EventTableSerialTh, EventTableSerialTd } from '../Common/EventTableSerial';
import {
  money,
  EventPaymentInstructions,
  statusPillClass,
  formatPublicPaymentStatusPillLabel,
  formatPublicPaymentLinkPillLabel,
  PaymentConfirmedCard,
  PaymentPendingApprovalCard,
  PaymentRejectedCard,
  PublicPaymentPageLoading,
  PublicPaymentPageError,
  EventPaymentTermsModal,
  publicPaymentPageShellClass,
  publicPaymentPageContainerClass,
  publicPaymentHeaderProps,
} from '../Common/eventPublicPaymentUi';

const PublicSponsorContractPaymentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const paycorpCallbackStatus = String(searchParams.get('paycorp') || '').toLowerCase();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [data, setData] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [paymentAgreementText, setPaymentAgreementText] = useState('');
  const [method, setMethod] = useState('CARD');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getPublicSponsorContractPayment(token);
      const payload = res?.data?.data ?? null;
      setData(payload);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Could not load payment page';
      toast.error(msg);
      setLoadError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPublicEventSettings()
      .then((res) => setPaymentAgreementText(String(res.data?.data?.paymentAgreementText || '').trim()))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setLoadError('Payment link is missing from the URL.');
      return;
    }
    load();
  }, [token]);

  useEffect(() => {
    if (!paycorpCallbackStatus) return;
    if (paycorpCallbackStatus === 'success') {
      toast.success('Card payment completed successfully', { id: 'public-scp-paycorp-success' });
      load();
    } else if (paycorpCallbackStatus === 'failed' || paycorpCallbackStatus === 'error') {
      toast.error('Card payment failed', { id: 'public-scp-paycorp-failed' });
    } else if (paycorpCallbackStatus === 'cancelled') {
      toast.error('Card payment was cancelled', { id: 'public-scp-paycorp-cancelled' });
    }
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [paycorpCallbackStatus]);

  const market = data?.pricingMarket || 'LOCAL';
  const isTaxInvoice = String(data?.billingInvoiceType || '').toUpperCase() === 'TAX_INVOICE';
  const isPaid = String(data?.paymentStatus || '').toUpperCase() === 'PAID';
  const isPendingApproval = String(data?.paymentStatus || '').toUpperCase() === 'PENDING_APPROVAL';
  const isRejected = String(data?.paymentStatus || '').toUpperCase() === 'REJECTED';
  const invoiceTotal = useMemo(
    () => Number(data?.invoice?.grandTotal || data?.billingInvoice?.grandTotal || data?.taxInvoice?.grandTotal || 0),
    [data]
  );
  const amountPaid = useMemo(() => Number(data?.amountPaid || 0), [data]);
  const amountDue = useMemo(() => Number(data?.amountDue ?? invoiceTotal), [data, invoiceTotal]);
  const payableEvents = useMemo(
    () => (data?.funding?.events || []).filter((e) => Number(e.balance) > 0.005),
    [data]
  );
  const [selectedEvents, setSelectedEvents] = useState([]);
  useEffect(() => {
    setSelectedEvents(payableEvents.map((e) => e.event));
  }, [payableEvents]);
  const selectedTotal = useMemo(() => {
    const cents = payableEvents
      .filter((e) => selectedEvents.includes(e.event))
      .reduce((s, e) => s + Math.round(Number(e.balance) * 100), 0);
    return Math.min(cents / 100, amountDue > 0 ? amountDue : cents / 100);
  }, [payableEvents, selectedEvents, amountDue]);
  const hasEventPicker = payableEvents.length > 0;
  const total = hasEventPicker && !isPaid
    ? selectedTotal
    : (amountDue > 0 && !isPaid ? amountDue : invoiceTotal);
  const toggleEvent = (id) => setSelectedEvents((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  const eventsLabel = (data?.eventNames || []).filter(Boolean).join(' / ')
    || data?.event?.name
    || 'SLIM Events';
  const linkActive = String(data?.paymentLinkStatus || '').toUpperCase() === 'ACTIVE';
  const canShowPaymentForm = !isPaid && !isPendingApproval && linkActive;

  const canProceedPayment = acceptedTerms && !submitting && canShowPaymentForm
    && (!hasEventPicker || (selectedEvents.length > 0 && total > 0));
  const canSubmitOffline =
    canProceedPayment
    && (method === 'BANK_TRANSFER' || method === 'CHEQUE_DEPOSIT')
    && Boolean(referenceNumber.trim())
    && Boolean(String(paymentDate || '').trim())
    && receipts.length > 0;

  const onPickReceipt = async (fileOrEvent) => {
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      if (fileOrEvent?.target) fileOrEvent.target.value = '';
      return;
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(String(file.type || '').toLowerCase())) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      if (fileOrEvent?.target) fileOrEvent.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const uploadRes = await uploadFilePublic(file, {
        category: 'SPONSOR_CONTRACT_PAYMENT_RECEIPT',
        description: `Sponsor contract payment receipt: ${data?.contractNumber || ''}`.trim(),
        title: file.name,
      });
      const f = uploadRes?.data?.data || uploadRes?.data || {};
      setReceipts([{
        fileId: f.fileId || f._id || '',
        fileName: f.fileName || file.name,
        originalName: f.originalName || file.name,
        fileSize: f.fileSize || file.size,
        fileType: f.fileType || file.type,
      }]);
      toast.success('Receipt uploaded');
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Receipt upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileOrEvent?.target) {
        fileOrEvent.target.value = '';
      }
    }
  };

  const payCard = async () => {
    if (!acceptedTerms) return toast.error('Please accept the payment terms and conditions before paying');
    setSubmitting(true);
    try {
      const res = await initiatePublicSponsorContractCardPayment(token, {
        amount: total,
        eventIds: hasEventPicker ? selectedEvents : undefined,
        paymentAgreement: { agreed: true, agreedAtClient: new Date().toISOString(), source: 'public_sponsor_contract_payment' },
      });
      const url = res?.data?.data?.paymentPageUrl;
      if (!url) throw new Error('Gateway url missing');
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Could not start card payment');
    } finally {
      setSubmitting(false);
    }
  };

  const payOffline = async () => {
    if (!acceptedTerms) return toast.error('Please accept the payment terms and conditions before submitting');
    if (!referenceNumber.trim()) return toast.error('Reference number is required');
    if (!String(paymentDate || '').trim()) return toast.error('Payment date is required');
    if (!receipts.length) return toast.error('At least one receipt is required');
    setSubmitting(true);
    try {
      await submitPublicSponsorContractOfflinePayment(token, {
        method,
        amount: total,
        eventIds: hasEventPicker ? selectedEvents : undefined,
        referenceNumber,
        paymentDate,
        receiptDocuments: receipts,
        paymentAgreement: { agreed: true },
      });
      toast.success('Payment submitted for verification');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadBillingInvoice = async () => {
    try {
      const res = await downloadPublicSponsorContractTaxInvoicePdf(token);
      triggerBlobDownload(res.data, `${String((isTaxInvoice ? data?.taxInvoice?.invoiceNumber : data?.invoice?.invoiceNumber) || (isTaxInvoice ? 'tax-invoice' : 'invoice')).replace(/[/\\?%*:|"<>\s]+/g, '-')}.pdf`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Invoice not available');
    }
  };

  const downloadReceiptFor = async (entry) => {
    try {
      const res = await downloadPublicSponsorContractReceiptPdf(token, entry.paymentKey);
      triggerBlobDownload(res.data, `${String(entry.receiptNumber || 'receipt').replace(/[/\\?%*:|"<>\s]+/g, '-')}.pdf`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Receipt not available');
    }
  };

  const downloadReceipt = async () => {
    try {
      const res = await downloadPublicSponsorContractReceiptPdf(token, 'latest');
      triggerBlobDownload(res.data, `${String([...(data?.paymentReceipts || [])].reverse().find((r) => r?.receiptNumber)?.receiptNumber || 'receipt').replace(/[/\\?%*:|"<>\s]+/g, '-')}.pdf`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Receipt not available yet');
    }
  };

  if (loading) return <PublicPaymentPageLoading />;
  if (!data) return <PublicPaymentPageError message={loadError} />;

  const lineItems = Array.isArray(data.invoice?.lineItems) ? data.invoice.lineItems : [];
  const billingInv = data.billingInvoice;
  const taxInv = data.taxInvoice;
  const contractHeaderTitle = `Sponsor contract payment • ${data.contractNumber || '—'}`;
  const eventNameCount = (data.eventNames || []).filter(Boolean).length;
  const showRightEventLogo = eventNameCount <= 1 && Boolean(data.eventLogoUrl);

  return (
    <div className={publicPaymentPageShellClass}>
      <div className={publicPaymentPageContainerClass}>
        <PublicSlimHeader
          title={contractHeaderTitle}
          {...publicPaymentHeaderProps}
          rightLogoSrc={showRightEventLogo ? data.eventLogoUrl : ''}
          rightLogoAlt="Event logo"
        />

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Contract information</h2>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                <p className="text-gray-700 sm:col-span-2">
                  <span className="font-medium text-gray-900">Event(s):</span> {eventsLabel}
                </p>
                <p className="text-gray-700">
                  <span className="font-medium text-gray-900">Contract no.:</span> {data.contractNumber || '—'}
                </p>
                <p className="text-gray-700">
                  <span className="font-medium text-gray-900">Sponsor:</span> {data.sponsorName || '—'}
                </p>
                <p className="text-gray-700">
                  <span className="font-medium text-gray-900">Contact:</span> {data.contactName || '—'}
                </p>
                <p className="text-gray-700">
                  <span className="font-medium text-gray-900">Contact email:</span> {data.contactEmail || '—'}
                </p>
                {data.linkExpiresAt && !data.linkExpired && !isPaid ? (
                  <p className="text-gray-700 sm:col-span-2">
                    <span className="font-medium text-gray-900">This payment link is valid until:</span>{' '}
                    {new Date(data.linkExpiresAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${statusPillClass(data.paymentStatus)}`}>
                Payment: {formatPublicPaymentStatusPillLabel(data)}
              </span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${statusPillClass(data.paymentLinkStatus)}`}>
                Link: {formatPublicPaymentLinkPillLabel(data.paymentLinkStatus)}
              </span>
            </div>
          </div>

          {(taxInv?.invoiceNumber || billingInv?.invoiceNumber) && !isPaid && (
            <div className="mt-3 flex flex-wrap gap-2">
              {taxInv?.invoiceNumber && isTaxInvoice && (
                <button
                  type="button"
                  onClick={downloadBillingInvoice}
                  className="px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-800 text-sm hover:bg-indigo-100"
                >
                  Download Tax Invoice ({taxInv.invoiceNumber})
                </button>
              )}
              {billingInv?.invoiceNumber && !isTaxInvoice && (
                <button
                  type="button"
                  onClick={downloadBillingInvoice}
                  className="px-3 py-1.5 rounded-md border border-sky-200 bg-sky-50 text-sky-800 text-sm hover:bg-sky-100"
                >
                  Download Invoice ({billingInv.invoiceNumber})
                </button>
              )}
            </div>
          )}
        </div>

        {data.sponsorSummary ? (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Your sponsorship at a glance</h2>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
              {[
                { label: 'Contracts', value: data.sponsorSummary.contractCount },
                { label: 'Events sponsored', value: data.sponsorSummary.eventCount },
                { label: 'Total committed', value: money(data.sponsorSummary.totalCommitted, market) },
                { label: 'Paid so far', value: money(data.sponsorSummary.totalPaid, market), tone: 'text-green-700' },
                { label: 'Still to pay', value: money(data.sponsorSummary.totalOutstanding, market), tone: 'text-amber-700' },
              ].map((c) => (
                <div key={c.label} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="text-xs text-gray-500">{c.label}</div>
                  <div className={`text-base font-semibold ${c.tone || 'text-gray-900'}`}>{c.value}</div>
                </div>
              ))}
            </div>
            {data.sponsorSummary.contracts?.length > 1 ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="py-1.5 pr-4 font-semibold">Contract</th>
                      <th className="py-1.5 pr-4 font-semibold">Events</th>
                      <th className="py-1.5 pr-4 text-right font-semibold">Total</th>
                      <th className="py-1.5 pr-4 text-right font-semibold">Paid</th>
                      <th className="py-1.5 text-right font-semibold">To pay</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.sponsorSummary.contracts.map((c) => (
                      <tr key={c.contractNumber} className={c.isCurrent ? 'bg-indigo-50/50' : ''}>
                        <td className="py-2 pr-4 font-mono text-xs text-gray-900">{c.contractNumber}{c.isCurrent ? ' (this page)' : ''}</td>
                        <td className="py-2 pr-4 text-gray-700">{c.events.map((e) => e.eventName).filter(Boolean).join(', ') || '—'}</td>
                        <td className="py-2 pr-4 text-right">{money(c.total, market)}</td>
                        <td className="py-2 pr-4 text-right text-green-700">{money(c.paid, market)}</td>
                        <td className="py-2 text-right">{money(c.due, market)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        {data.funding?.events?.length ? (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Payment status by event</h2>
            <p className="mt-1 text-xs text-gray-600">An event is supported by your sponsorship once its share has been paid and approved.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {data.funding.events.map((e) => {
                const pct = e.gross > 0 ? Math.min(100, Math.round((e.paid / e.gross) * 100)) : 0;
                const tone = e.status === 'PAID' ? 'bg-green-100 text-green-800' : e.status === 'PARTIALLY_PAID' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-900';
                return (
                  <div key={e.event} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{e.eventName || 'Event'}</div>
                        <div className="text-xs text-gray-500">
                          {e.startDate ? new Date(e.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Date to be announced'}
                        </div>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
                        {e.status === 'PAID' ? 'Paid' : e.status === 'PARTIALLY_PAID' ? 'Partly paid' : 'Payment due'}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
                      <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-2 flex justify-between text-xs text-gray-600">
                      <span>Paid {money(e.paid, market)} of {money(e.gross, market)}</span>
                      {e.balance > 0 ? <span className="font-medium text-amber-700">Due {money(e.balance, market)}</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {isPaid && (
          <PaymentConfirmedCard
            data={data}
            confirmedDescription="Your sponsorship contract payment has been received. Thank you for partnering with SLIM."
            onDownloadTaxInvoice={
              taxInv?.invoiceNumber && isTaxInvoice ? downloadBillingInvoice : null
            }
            onDownloadBillingInvoice={
              billingInv?.invoiceNumber && !isTaxInvoice ? downloadBillingInvoice : null
            }
            onDownloadReceipt={downloadReceipt}
          />
        )}

        {isPendingApproval && (
          <PaymentPendingApprovalCard data={data} contactEmail={data.contactEmail} />
        )}

        {isRejected && <PaymentRejectedCard data={data} />}

        {lineItems.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-3 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Invoice breakdown</h2>
              {data.invoice?.invoiceNumber ? (
                <p className="mt-1 text-xs text-gray-600">Proforma / invoice ref: {data.invoice.invoiceNumber}</p>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <EventTableSerialTh className="px-4 py-2.5" />
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Description</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-700">Quantity</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-700">Rate</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {lineItems.map((line, idx) => {
                    const qty = Number(line.quantity) || 0;
                    const amount = Number(line.amount) || 0;
                    const rate = Number(line.rate) || (qty > 0 ? amount / qty : 0);
                    return (
                      <tr key={`${line.description || 'line'}-${idx}`}>
                        <EventTableSerialTd order="desc" rowCount={lineItems.length} index={idx} className="px-4 py-2.5" />
                        <td className="px-4 py-2.5 text-gray-800">{line.description || 'Line item'}</td>
                        <td className="px-4 py-2.5 text-right text-gray-800">
                          {qty ? qty.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-800">{money(rate, market)}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-gray-900">{money(amount, market)}</td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td className="px-4 py-2.5" colSpan={3} />
                    <td className="px-4 py-2.5 text-gray-700">Sub total</td>
                    <td className="px-4 py-2.5 text-right text-gray-800">{money(data.invoice?.subTotal, market)}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5" colSpan={3} />
                    <td className="px-4 py-2.5 text-gray-700">Tax total</td>
                    <td className="px-4 py-2.5 text-right text-gray-800">{money(data.invoice?.taxTotal, market)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3" colSpan={3} />
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">Grand total</td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{money(invoiceTotal, market)}</td>
                  </tr>
                  {amountPaid > 0 && (
                    <tr>
                      <td className="px-4 py-2.5" colSpan={3} />
                      <td className="px-4 py-2.5 text-gray-700">Already paid</td>
                      <td className="px-4 py-2.5 text-right text-gray-800">{money(amountPaid, market)}</td>
                    </tr>
                  )}
                  {amountPaid > 0 && amountDue > 0 && (
                    <tr className="bg-amber-50">
                      <td className="px-4 py-3" colSpan={3} />
                      <td className="px-4 py-3 text-sm font-semibold text-amber-950">Amount due</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-amber-950">{money(amountDue, market)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!lineItems.length && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5">
            <p className="text-sm text-gray-600">Amount due</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{money(total, market)}</p>
          </div>
        )}

        {data.paymentDisplay && (
          <EventPaymentInstructions
            paymentDisplay={data.paymentDisplay}
            bankAccount={data.bankAccount}
            showCardSettlementNote={false}
          />
        )}

        {canShowPaymentForm && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              {isRejected
                ? 'Re-submit payment'
                : (hasEventPicker ? 'Make a payment' : 'Make payment (full amount required)')}
            </h2>
            {hasEventPicker ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-900">Which events are you paying for?</p>
                <p className="mb-2 text-xs text-gray-600">Each event is supported once its share is paid. You can pay for some events now and the rest later.</p>
                <div className="space-y-1.5">
                  {payableEvents.map((e) => (
                    <label key={e.event} className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm hover:border-indigo-300">
                      <span className="flex min-w-0 items-center gap-2">
                        <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600" checked={selectedEvents.includes(e.event)} onChange={() => toggleEvent(e.event)} />
                        <span className="truncate text-gray-900">{e.eventName || 'Event'}</span>
                      </span>
                      <span className="shrink-0 font-medium text-gray-900">{money(e.balance, market)}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 text-sm font-semibold text-gray-900">
                  <span>Amount to pay now</span>
                  <span>{money(total, market)}</span>
                </div>
                {selectedEvents.length === 0 ? <p className="mt-1 text-xs text-red-700">Select at least one event.</p> : null}
              </div>
            ) : null}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <label className="flex items-start gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I have read and agree to the payment terms and conditions.{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="underline font-semibold hover:text-amber-950"
                  >
                    Show terms and conditions
                  </button>
                </span>
              </label>
            </div>

            {!acceptedTerms && (
              <div className="text-xs rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2">
                Payment cannot continue until you accept the payment terms and conditions.
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {[
                { value: 'CARD', label: 'Card (Paycorp)' },
                { value: 'BANK_TRANSFER', label: 'Bank transfer' },
                { value: 'CHEQUE_DEPOSIT', label: 'Cheque deposit' },
              ].map((m) => {
                const checked = method === m.value;
                return (
                  <label
                    key={m.value}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition-colors ${
                      checked
                        ? 'border-indigo-200 bg-indigo-50 text-indigo-800'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={checked}
                      onChange={() => setMethod(m.value)}
                      className="accent-indigo-600"
                    />
                    {m.label}
                  </label>
                );
              })}
            </div>

            {method === 'CARD' ? (
              <button
                type="button"
                onClick={payCard}
                disabled={!canProceedPayment}
                className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Redirecting…' : `Pay ${money(total, market)} by card`}
              </button>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction / cheque reference <span className="text-red-600">*</span>
                  </label>
                  <input
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="Enter transaction / cheque reference"
                    autoComplete="off"
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-full sm:w-56 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
                  />
                </div>
                <ProofAttachmentDropzone
                  file={receipts[0] || null}
                  required
                  hint={null}
                  busy={uploading}
                  onChange={(file) => {
                    if (!file) {
                      setReceipts([]);
                      return;
                    }
                    onPickReceipt(file);
                  }}
                />
                <button
                  type="button"
                  onClick={payOffline}
                  disabled={!canSubmitOffline || uploading}
                  className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting…' : 'Submit offline payment for approval'}
                </button>
              </div>
            )}
          </div>
        )}

        {data.linkExpired && !isPaid ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            This payment link expired
            {data.linkExpiresAt
              ? ` on ${new Date(data.linkExpiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
              : ''}
            . You can still see your payments and download receipts below. To pay the remaining balance, please contact the event team and ask them to extend the link.
          </div>
        ) : null}

        {!canShowPaymentForm && !isPaid && !isPendingApproval && !isRejected && !data.linkExpired && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5 text-sm text-gray-700">
            This payment link is no longer active. Contact the event team if you need assistance.
          </div>
        )}

        {data.creditHeld > 0.005 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            You have {money(data.creditHeld, market)} of extra payment on account with SLIM. Contact the event team to apply it to another contract or arrange a refund.
          </div>
        ) : null}

        {data.paymentHistory?.length ? (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-3 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Payment history</h2>
              <p className="mt-1 text-xs text-gray-600">Every payment you have made, with a receipt for each approved payment.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-700">
                    <th className="px-4 py-2.5 font-semibold">Date</th>
                    <th className="px-4 py-2.5 font-semibold">Reference</th>
                    <th className="px-4 py-2.5 font-semibold">Method</th>
                    <th className="px-4 py-2.5 font-semibold">For events</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
                    <th className="px-4 py-2.5 font-semibold">Status</th>
                    <th className="px-4 py-2.5 font-semibold">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white align-top">
                  {data.paymentHistory.map((p, idx) => {
                    const tone = p.isCreditNote ? 'bg-sky-100 text-sky-800'
                      : p.status === 'PAID' ? 'bg-green-100 text-green-800'
                      : p.status === 'PENDING_APPROVAL' ? 'bg-violet-100 text-violet-900'
                        : p.status === 'REJECTED' ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-700';
                    const label = p.isCreditNote ? 'Credit applied'
                      : p.status === 'PAID' ? 'Approved' : p.status === 'PENDING_APPROVAL' ? 'Awaiting verification'
                        : p.status === 'REJECTED' ? 'Rejected' : 'Reversed';
                    return (
                      <tr key={`${p.paymentKey || p.reference}-${idx}`} className={p.status === 'REVERSED' ? 'text-gray-500' : ''}>
                        <td className="px-4 py-2.5 whitespace-nowrap">{p.date ? new Date(p.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                        <td className="px-4 py-2.5">
                          <div className="font-mono text-xs text-gray-900">{p.receiptNumber || p.reference || '—'}</div>
                          {p.receiptNumber && p.reference ? <div className="text-xs text-gray-500">{p.reference}</div> : null}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap">{p.isCreditNote ? 'Credit note' : String(p.method || '—').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}</td>
                        <td className="px-4 py-2.5 text-xs">
                          {p.events?.length ? p.events.map((e) => (
                            <div key={e.event}>{e.eventName || 'Event'}{e.amount ? ` — ${money(e.amount, market)}` : ''}</div>
                          )) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          {money(p.amount, market)}
                          {p.overpaymentAmount > 0.005 && p.status === 'PAID' ? (
                            <div className="text-xs text-amber-700">Includes {money(p.overpaymentAmount, market)} extra held as credit</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{label}</span>
                          {p.rejectReason ? <div className="mt-1 max-w-[220px] text-xs text-red-700">Reason: {p.rejectReason}</div> : null}
                          {p.status === 'REJECTED' ? <div className="mt-1 text-xs text-gray-500">You can submit the payment again above.</div> : null}
                        </td>
                        <td className="px-4 py-2.5">
                          {p.paymentKey ? (
                            <button type="button" onClick={() => downloadReceiptFor(p)} className="rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-100">
                              Download receipt
                            </button>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {data.activity?.length ? (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Activity history</h2>
            <ol className="mt-3 space-y-3">
              {data.activity.map((a, i) => (
                <li key={`${a.at}-${i}`} className="flex gap-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-indigo-400" />
                  <div className="text-sm">
                    <div className="text-gray-900">
                      {a.label}
                      {a.amount != null ? <span className="text-gray-600"> · {money(a.amount, market)}</span> : null}
                    </div>
                    <div className="text-xs text-gray-500">
                      {a.at ? new Date(a.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      {' · '}{a.byLabel}
                      {a.reference ? ` · Ref ${a.reference}` : ''}
                    </div>
                    {a.note ? <div className="text-xs text-gray-600">{a.note}</div> : null}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>

      <EventPaymentTermsModal
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        modalTitle="Payment agreement"
        paymentAgreementText={paymentAgreementText}
        useEventSettingsOnly
      />
    </div>
  );
};

export default PublicSponsorContractPaymentPage;
